import { Box, Button, CircularProgress, SxProps, Theme, Tooltip } from '@mui/material';
import React, { useState, useTransition } from 'react';
import { makeStyles, createStyles, } from '@mui/styles';
import { Add, DeleteForever, Refresh, Save, CloseSharp, RateReview, ArrowBackIosNew, Visibility } from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';
import i18n from '../../i18n';

/** 编辑图标（viewBox 0 0 32 32，置于 36px 图标钮内缩为 24） */
function ButtonEditOutlinedIcon() {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' width={24} height={24} viewBox='0 0 32 32' fill='none' aria-hidden>
            <path
                d='M25.2492 6.14523L25.8542 6.75023C26.3242 7.22023 26.3242 7.98023 25.8542 8.44523L24.3992 9.90523L22.0942 7.60023L23.5492 6.14523C24.0192 5.67523 24.7792 5.67523 25.2442 6.14523H25.2492ZM13.6892 16.0102L20.3992 9.29523L22.7042 11.6002L15.9892 18.3102C15.8442 18.4552 15.6642 18.5602 15.4692 18.6152L12.5442 19.4502L13.3792 16.5252C13.4342 16.3302 13.5392 16.1502 13.6842 16.0052L13.6892 16.0102ZM21.8542 4.45023L11.9892 14.3102C11.5542 14.7452 11.2392 15.2802 11.0742 15.8652L9.64422 20.8652C9.52422 21.2852 9.63922 21.7352 9.94922 22.0452C10.2592 22.3552 10.7092 22.4702 11.1292 22.3502L16.1292 20.9202C16.7192 20.7502 17.2542 20.4352 17.6842 20.0052L27.5492 10.1452C28.9542 8.74023 28.9542 6.46023 27.5492 5.05523L26.9442 4.45023C25.5392 3.04523 23.2592 3.04523 21.8542 4.45023ZM7.59922 6.40023C5.16922 6.40023 3.19922 8.37023 3.19922 10.8002V24.4002C3.19922 26.8302 5.16922 28.8002 7.59922 28.8002H21.1992C23.6292 28.8002 25.5992 26.8302 25.5992 24.4002V18.8002C25.5992 18.1352 25.0642 17.6002 24.3992 17.6002C23.7342 17.6002 23.1992 18.1352 23.1992 18.8002V24.4002C23.1992 25.5052 22.3042 26.4002 21.1992 26.4002H7.59922C6.49422 26.4002 5.59922 25.5052 5.59922 24.4002V10.8002C5.59922 9.69523 6.49422 8.80023 7.59922 8.80023H13.1992C13.8642 8.80023 14.3992 8.26523 14.3992 7.60023C14.3992 6.93523 13.8642 6.40023 13.1992 6.40023H7.59922Z'
                fill='currentColor'
            />
        </svg>
    );
}

export enum LoadingOption {
    None, Avoid, AvoidNoLoading
}

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            minWidth: '36px !important',
            minHeight: '36px !important',
            height: '36px !important',
            width: '36px !important',
            padding: '0px !important'
            // '&.Mui-disabled': {
            //     backgroundColor: '#00000011',
            // }
        },
        secondRoot: {
            minHeight: '36px !important',
            height: '36px !important',
        }
    })
)
/**
 * 正方形按钮组件(icon)
 * - 默认样式，宽高
 */
export default function ButtonComponent(
    props: ButtonComponentType
): JSX.Element {
    useTransition();
    const { size, color, children, variant = 'contained', titleText, action, handleClick,
        disabled = false, extraButtonStyle = '', forbidConfirm = LoadingOption.None, buttonStyle, sx, loadingStyle,
        space = true, iconBtn = true, customIcon
    } = props
    const classes = useStyles();
    const [isLoading, setLoad] = useState(false);
    /** 删除：未显式指定 color 时使用主题 Error 功能色 */
    const buttonColor = color !== undefined ? color : action === 'DELETE' ? 'error' : undefined;
    const actionIcon: Record<string, IconObj> = {
        'ADD': { com: <Add />, title: i18n.t('general_text_add'), },
        'DELETE': { com: <DeleteForever />, title: i18n.t('general_text_delete'), },
        'REFRESH': { com: <Refresh />, title: i18n.t('general_text_refresh'), },
        'SAVE': { com: <Save />, title: i18n.t('general_text_save'), },
        'CLOSE': { com: <CloseSharp />, title: i18n.t('general_button_cancel'), },
        'VIEW': { com: <RateReview />, title: i18n.t('general_text_details'), },
        'BACK': { com: <ArrowBackIosNew />, title: i18n.t('google_text_return'), },
        'RESTORE': { com: <ReplayIcon />, title: i18n.t('recycle_bin_page_text_restore_btn') },
        'DETAIL': { com: <Visibility />, title: i18n.t('general_text_details'), },
        'EDIT': { com: <ButtonEditOutlinedIcon />, title: i18n.t('general_text_edit') },
    };
    const getActionIcon = (action = '', children?: JSX.Element | string): IconObj => {
        return {
            com: actionIcon[action]?.com || (typeof children === 'string' ? <>{children}</> : children || <></>),
            title: titleText || actionIcon[action]?.title || '',
        };
    };
    const ActionIcon = () => {
        const { com, title } = getActionIcon(action, children);
        /** 指定时覆盖 action 默认图标（如表格行删除用轮廓垃圾桶） */
        return { com: customIcon ?? com, title }
    }


    // *********************
    // View
    // *********************
    return (
        <Tooltip title={ActionIcon().title} placement='top' >
            <span style={space ? { padding: '8px', marginLeft: '-8px' } : {}}>
                <Button
                    variant={variant}
                    size={size}
                    color={buttonColor}
                    disabled={disabled || forbidConfirm !== 0 || isLoading ? true : false}
                    className={`${typeof children === 'string' || !iconBtn ? classes.secondRoot : classes.root} ${extraButtonStyle} `}
                    onClick={async (e) => {
                        e.stopPropagation();
                        setLoad(true)
                        await handleClick?.(e);
                        setLoad(false);
                    }}
                    style={{ ...((disabled || forbidConfirm !== 0 || isLoading ? true : false) ? { borderColor: ' #999' } : {}), ...buttonStyle }}
                    sx={sx}
                >
                    {
                        isLoading &&
                        <Box style={{
                            backgroundColor: '#fff3',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1,
                            borderRadius: 'inherit',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <CircularProgress
                                color='primary'
                                size={'20px'}
                                style={{
                                    ...loadingStyle
                                }} />
                        </Box>
                    }
                    {ActionIcon().com}
                </Button >
            </span>
        </Tooltip >
    )
}

type IconObj = {
    com: JSX.Element;
    title: string;
};

export type ButtonComponentType = {
    // 按钮尺寸
    size?: 'large' | 'medium' | 'small';
    // 按钮颜色（未传且 action 为 DELETE 时默认 error，即设计「功能色 / Error」）
    color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
    // 按钮类型
    variant?: 'contained' | 'outlined' | 'text';
    // 按钮图标或文字  自定义
    children?: JSX.Element | string;
    // 点击事件
    handleClick?: (e) => void | Promise<void>;
    // 是否禁用
    disabled?: boolean;
    // 按钮样式
    extraButtonStyle?: string;
    // 按钮文字样式
    extraTitleStyle?: string;
    // 按钮操作类型
    action?: 'ADD' | 'DELETE' | 'REFRESH' | 'SAVE' | 'CLOSE' | 'VIEW' | 'BACK' | 'RESTORE' | 'DETAIL' | 'EDIT';
    // 按钮标题提示文字
    titleText?: string;
    // 是否禁止掉确定按钮，主要这里开启loading，确定交互 0不禁止 1 禁止 2禁止不开启loading ...
    forbidConfirm?: LoadingOption;
    //
    buttonStyle?: React.CSSProperties

    async?: boolean;

    sx?: SxProps<Theme>
    loadingStyle?: React.CSSProperties,
    // 间隔
    space?: boolean;
    // 图标按钮
    iconBtn?: boolean;
    /** 覆盖 action 对应的默认图标，tooltip 仍取自 action / titleText */
    customIcon?: JSX.Element;
}

